import React, { PureComponent } from 'react';
import { defineMessages, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button } from '@lifeomic/chroma-react/components/Button';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { getTestProps } from '../../../util/testProps';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import clsx from 'clsx';
import { SurveyOverrideClasses } from './SurveyOverrideClasses';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { withSurveyContext, WithSurveyContextInjectedProps } from './Contexts';

export const testIds = {
  confirmSurveySubmit: 'surveySubmitDialog-confirm-button',
  cancelSurveySubmit: 'surveySubmitDialog-cancel-button',
  modal: 'surveySubmitDialog-modal',
  submit: 'survey-submit',
};

const messages = defineMessages({
  buttonText: {
    id: 'surveys.submit.buttonText',
    defaultMessage: 'Complete',
  },
  cancel: {
    id: 'surveys.submit.cancel',
    defaultMessage: 'Back to survey',
  },
  confirm: {
    id: 'surveys.submit.confirm',
    defaultMessage: 'Yes, submit',
  },
  dialogHeader: {
    id: 'surveys.submit.dialogHeader',
    defaultMessage: 'Are you sure you want to submit your survey?',
  },
  dialogContent: {
    id: 'surveys.submit.dialogContent',
    defaultMessage:
      'You will not be able to change your answers once it is submitted.',
  },
});

export interface DialogText {
  cancelButtonText: string;
  confirmButtonText: string;
  dialogHeader: string;
  dialogContent: string;
  buttonText: string;
}

const styles = (theme: Theme) =>
  createStyles({
    buttonContainer: {
      padding: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerContainer: {
      padding: theme.spacing(2),
    },
    contentContainer: {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(4),
    },
  });

export interface SubmitButtonOwnProps {
  disabled?: boolean;
  submitForm: () => void;
  dialogText: DialogText;
  overrideClasses: SurveyOverrideClasses;
}

export type SubmitButtonProps = SubmitButtonOwnProps &
  WrappedComponentProps &
  WithStyles<typeof styles> &
  WithSurveyContextInjectedProps;

export interface SubmitButtonState {
  isDialogOpen: boolean;
}

class SubmitButton extends PureComponent<SubmitButtonProps, SubmitButtonState> {
  public state: SubmitButtonState = { isDialogOpen: false };

  handleOpenDialog = () => {
    this.setState({ isDialogOpen: true });
  };

  handleCloseDialog = () => {
    this.setState({ isDialogOpen: false });
  };

  handleSubmit = () => {
    this.handleCloseDialog();
    this.props.submitForm();
  };

  render() {
    const {
      classes,
      disabled,
      intl: { formatMessage },
      dialogText = {} as DialogText,
      overrideClasses,
      survey,
    } = this.props;

    const { isDialogOpen } = this.state;

    return (
      <React.Fragment>
        <Button
          className={clsx(overrideClasses.submitButtonClass)}
          aria-haspopup="dialog"
          aria-expanded={isDialogOpen}
          disabled={disabled}
          variant="contained"
          onClick={this.handleOpenDialog}
          {...getTestProps(testIds.submit)}
        >
          {dialogText.buttonText || formatMessage(messages.buttonText)}
        </Button>
        <Modal
          size={0}
          onDismiss={this.handleCloseDialog}
          isOpen={isDialogOpen}
          justifyActions="space-between"
          {...getTestProps(testIds.modal)}
          title={
            survey?.getCustomMessage('submission-header') ||
            dialogText.dialogHeader ||
            formatMessage(messages.dialogHeader)
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={this.handleCloseDialog}
                {...getTestProps(testIds.cancelSurveySubmit)}
              >
                {survey?.getCustomMessage('submission-reject') ||
                  dialogText.cancelButtonText ||
                  formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="contained"
                onClick={this.handleSubmit}
                {...getTestProps(testIds.confirmSurveySubmit)}
              >
                {survey?.getCustomMessage('submission-accept') ||
                  dialogText.confirmButtonText ||
                  formatMessage(messages.confirm)}
              </Button>
            </>
          }
        >
          <div className={classes.contentContainer}>
            <Text size="body">
              {survey?.getCustomMessage('submission-content') ||
                dialogText.dialogContent ||
                formatMessage(messages.dialogContent)}
            </Text>
          </div>
        </Modal>
      </React.Fragment>
    );
  }
}

export default withSurveyContext(injectIntl(withStyles(styles)(SubmitButton)));
